import {
  DEFAULT_LOCALE,
  DEFAULT_USE_CASE,
  isUseCase,
  type Locale,
  type TextDirection,
  type UseCase,
} from './types';

const RTL_LANGUAGES = new Set([
  'ar',
  'he',
  'fa',
  'ur',
  'ps',
  'sd',
  'ug',
  'yi',
  'dv',
  'ckb',
]);
const RTL_SCRIPTS = new Set(['arab', 'hebr', 'thaa', 'syrc', 'nkoo', 'adlm']);

/** Text direction for a locale, read from its language or an explicit script subtag. */
export function dirOf(locale: Locale): TextDirection {
  const [language = '', ...rest] = locale.toLowerCase().split('-');
  if (RTL_LANGUAGES.has(language)) return 'rtl';
  if (rest.some((subtag) => RTL_SCRIPTS.has(subtag))) return 'rtl';
  return 'ltr';
}

/**
 * Picks the best available locale for what was asked for, in order of
 * preference: an exact tag, then the language alone, then the same language
 * in another region, then `fallback`.
 */
export function resolveLocale(
  requested: readonly string[],
  available: readonly Locale[],
  fallback: Locale = DEFAULT_LOCALE,
): Locale {
  const byLower = new Map(
    available.map((tag) => [tag.toLowerCase(), tag] as const),
  );
  for (const raw of requested) {
    const tag = raw.trim().toLowerCase();
    if (!tag) continue;
    const exact = byLower.get(tag);
    if (exact) return exact;
    const [language = ''] = tag.split('-');
    const bare = byLower.get(language);
    if (bare) return bare;
    const sameLanguage = available.find((candidate) =>
      candidate.toLowerCase().startsWith(`${language}-`),
    );
    if (sameLanguage) return sameLanguage;
  }
  return available.includes(fallback) ? fallback : (available[0] ?? fallback);
}

/** Locales from an `Accept-Language` header, highest quality first; `*` is dropped. */
export function parseAcceptLanguage(
  header: string | null | undefined,
): string[] {
  if (!header) return [];
  return header
    .split(',')
    .map((part, index) => {
      const [tag = '', ...params] = part.trim().split(';');
      const q = params.map((p) => p.trim()).find((p) => p.startsWith('q='));
      const quality = q ? Number(q.slice(2)) : 1;
      return {
        tag: tag.trim(),
        quality: Number.isFinite(quality) ? quality : 0,
        index,
      };
    })
    .filter((entry) => entry.tag && entry.tag !== '*' && entry.quality > 0)
    .sort((a, b) => b.quality - a.quality || a.index - b.index)
    .map((entry) => entry.tag);
}

export interface I18nContextInput {
  readonly user?:
    | { readonly locale?: string | null | undefined }
    | null
    | undefined;
  readonly workspace?:
    | {
        readonly locale?: string | null | undefined;
        readonly useCase?: string | null | undefined;
        readonly countryCode?: string | null | undefined;
      }
    | null
    | undefined;
  readonly organization?:
    | {
        readonly defaultLocale?: string | null | undefined;
        readonly useCase?: string | null | undefined;
        readonly countryCode?: string | null | undefined;
      }
    | null
    | undefined;
  /** `navigator.languages` or the parsed `Accept-Language` header, in order. */
  readonly requested?: readonly string[] | undefined;
  /** Locales the caller has catalogues for. */
  readonly available: readonly Locale[];
}

/** Which setting a resolved value came from. */
export type I18nSource =
  | 'user'
  | 'workspace'
  | 'organisation'
  | 'request'
  | 'default';

export interface I18nContext {
  readonly locale: Locale;
  readonly useCase: UseCase;
  readonly countryCode: string | undefined;
  readonly dir: TextDirection;
  /** Which setting decided the locale, and which decided the use case. */
  readonly localeFrom: I18nSource;
  readonly useCaseFrom: I18nSource;
}

/**
 * The same resolution in the browser and on the server: a person's own
 * locale, then the workspace's, then the organisation's, then what the
 * client asked for. Use case and country come from the workspace, then the
 * organisation.
 */
export function resolveI18nContext(input: I18nContextInput): I18nContext {
  const localeLayers = (
    [
      { from: 'user', tag: input.user?.locale ?? '' },
      { from: 'workspace', tag: input.workspace?.locale ?? '' },
      { from: 'organisation', tag: input.organization?.defaultLocale ?? '' },
    ] satisfies ReadonlyArray<{ from: I18nSource; tag: string }>
  ).filter((layer) => layer.tag.trim() !== '');
  const asked = (input.requested ?? []).filter((tag) => tag.trim() !== '');
  const locale = resolveLocale(
    [...localeLayers.map((layer) => layer.tag), ...asked],
    input.available,
  );
  const chosen = localeLayers.find((layer) => layer.tag === locale);
  const localeFrom: I18nSource = chosen
    ? chosen.from
    : asked.length > 0
      ? 'request'
      : 'default';
  const useCaseLayers: ReadonlyArray<{ from: I18nSource; value: unknown }> = [
    { from: 'workspace', value: input.workspace?.useCase },
    { from: 'organisation', value: input.organization?.useCase },
  ];
  const useCaseLayer = useCaseLayers.find((layer) => isUseCase(layer.value));
  const useCase = isUseCase(useCaseLayer?.value)
    ? useCaseLayer.value
    : DEFAULT_USE_CASE;
  const countryCode = [
    input.workspace?.countryCode,
    input.organization?.countryCode,
  ].find((code) => typeof code === 'string' && code.trim() !== '');
  return {
    locale,
    useCase,
    countryCode: countryCode?.toUpperCase(),
    dir: dirOf(locale),
    localeFrom,
    useCaseFrom: useCaseLayer?.from ?? 'default',
  };
}
